// src/components/ParticleBackground.tsx

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const ParticleCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
`;

const PersistentCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
  pointer-events: none;
`;

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
}

const ParticleBackground: React.FC = () => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const persistentCanvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    const persistentCanvas = persistentCanvasRef.current;
    if (!canvas || !persistentCanvas) return;

    const ctx = canvas.getContext('2d');
    const persistentCtx = persistentCanvas.getContext('2d');
    if (!ctx || !persistentCtx) return;

    // Don't clear canvases immediately - let the transition happen naturally
    // The animation loop will handle the color transition gradually
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const persistentCanvas = persistentCanvasRef.current;
    if (!canvas || !persistentCanvas) return;

    const ctx = canvas.getContext('2d');
    const persistentCtx = persistentCanvas.getContext('2d');
    if (!ctx || !persistentCtx) return;

    // Set canvas sizes
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      persistentCanvas.width = window.innerWidth;
      persistentCanvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create particles
    const createParticles = () => {
      const particles: Particle[] = [];
      // Optimized particle count based on screen size
      const particleCount = Math.min(window.innerWidth * window.innerHeight / 15000, 100);

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5, // Smaller particles for better performance
          speedX: (Math.random() - 0.5) * 0.3, // Slower movement
          speedY: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.4 + 0.2 // More subtle opacity
        });
      }

      particlesRef.current = particles;
    };

    createParticles();

    // Animation loop with frame rate limiting
    let lastTime = 0;
    const frameInterval = 1000 / 60; // Target 60fps

    const animate = (time: number) => {
      if (!ctx) return;

      // Frame rate limiting
      if (time - lastTime < frameInterval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastTime = time;

      // Apply color transition to both canvases
      
      // Persistent canvas - gradual color transition
      persistentCtx.globalCompositeOperation = 'source-over';
      persistentCtx.fillStyle = theme === 'light' 
        ? 'rgba(255, 255, 255, 0.05)' // Faster fade to white in light mode
        : 'rgba(0, 0, 0, 0.05)'; // Faster fade to black in dark mode
      persistentCtx.fillRect(0, 0, persistentCanvas.width, persistentCanvas.height);

      // Main canvas - also apply gradual transition instead of complete clear
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = theme === 'light' 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;

      // Create spatial grid for optimized connection detection
      const gridSize = 100; // Same as connection distance
      const grid: Map<string, Particle[]> = new Map();

      // Helper function to get grid cell key
      const getGridKey = (x: number, y: number) => 
        `${Math.floor(x / gridSize)},${Math.floor(y / gridSize)}`;

      // First pass: update positions and populate grid
      particles.forEach(particle => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Bounce off edges
        if (particle.x <= 0 || particle.x >= canvas.width) {
          particle.speedX *= -1;
        }
        if (particle.y <= 0 || particle.y >= canvas.height) {
          particle.speedY *= -1;
        }

        // Add to spatial grid
        const key = getGridKey(particle.x, particle.y);
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key)!.push(particle);
      });

      // Draw all particles first (batch operation) - draw to both canvases
      // Use the same color transition approach for particles as well
      const targetParticleColor = theme === 'light' ? '0, 0, 0' : '255, 255, 255';
      
      particles.forEach(particle => {
        // Draw to main canvas
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${targetParticleColor}, ${particle.opacity})`;
        ctx.fill();

        // Also draw to persistent canvas for color persistence
        persistentCtx.beginPath();
        persistentCtx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        persistentCtx.fillStyle = `rgba(${targetParticleColor}, ${particle.opacity})`;
        persistentCtx.fill();
      });

      // Draw connections using spatial grid (optimized O(n) instead of O(n²))
      const targetConnectionColor = theme === 'light' ? '0, 0, 0' : '255, 255, 255';
      ctx.strokeStyle = `rgba(${targetConnectionColor}, 0.1)`;
      ctx.lineWidth = 0.5;
      
      const processedPairs = new Set<string>();
      
      particles.forEach(particle => {
        const cellX = Math.floor(particle.x / gridSize);
        const cellY = Math.floor(particle.y / gridSize);
        
        // Check particles in current cell and adjacent cells
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const key = `${cellX + dx},${cellY + dy}`;
            const cellParticles = grid.get(key) || [];
            
            cellParticles.forEach(otherParticle => {
              if (particle !== otherParticle) {
                // Create unique pair identifier to avoid duplicate connections
                const pairId = [particle, otherParticle]
                  .map(p => p.x + ',' + p.y)
                  .sort()
                  .join('|');
                
                if (!processedPairs.has(pairId)) {
                  processedPairs.add(pairId);
                  
                  const dx = particle.x - otherParticle.x;
                  const dy = particle.y - otherParticle.y;
                  const distanceSquared = dx * dx + dy * dy;
                  
                  if (distanceSquared < 10000) { // 100^2 = 10000
                    const distance = Math.sqrt(distanceSquared);
                    const connectionOpacity = 0.1 * (1 - distance / 100);
                    
                    // Draw to main canvas
                    ctx.beginPath();
                    ctx.strokeStyle = `rgba(${targetConnectionColor}, ${connectionOpacity})`;
                    ctx.moveTo(particle.x, particle.y);
                    ctx.lineTo(otherParticle.x, otherParticle.y);
                    ctx.stroke();

                    // Also draw to persistent canvas for color persistence
                    persistentCtx.beginPath();
                    persistentCtx.strokeStyle = `rgba(${targetConnectionColor}, ${connectionOpacity})`;
                    persistentCtx.lineWidth = 0.5;
                    persistentCtx.moveTo(particle.x, particle.y);
                    persistentCtx.lineTo(otherParticle.x, otherParticle.y);
                    persistentCtx.stroke();
                  }
                }
              }
            });
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [theme]);

  return (
    <>
      <PersistentCanvas ref={persistentCanvasRef} />
      <ParticleCanvas ref={canvasRef} />
    </>
  );
};

export default ParticleBackground;
